// Mini App "Content" page. Owns data fetching (TanStack Query hook from
// entities/message) and composes three widgets: activity-heatmap,
// top-posts, optimal-slots.
//
// Per the FSD layer graph, pages may import from widgets, features,
// entities, and shared. They may NOT import from another page.

import { useEffect } from "react";

import {
  CommunitySelector,
  selectActiveCommunityId,
  useActiveCommunityStore,
  useCommunitiesQuery,
} from "@entities/community";
import { useContentQuery } from "@entities/message";
import { ActivityHeatmap } from "@widgets/activity-heatmap";
import { OptimalSlotsPanel } from "@widgets/optimal-slots";
import { TopPostsBoard } from "@widgets/top-posts";

import { CONTENT_PAGE_STRINGS } from "../lib/strings";

import { ContentEmptyState } from "./ContentEmptyState";
import { ContentErrorState } from "./ContentErrorState";
import { ContentSkeleton } from "./ContentSkeleton";

export function ContentPage() {
  const activeCommunityId = useActiveCommunityStore(selectActiveCommunityId);
  const setActiveCommunityId = useActiveCommunityStore(
    (s) => s.setActiveCommunityId,
  );

  const communitiesQuery = useCommunitiesQuery();

  // Auto-select the first community as soon as the list resolves and the
  // store hasn't been seeded yet — same fallback as OverviewPage.
  useEffect(() => {
    if (activeCommunityId !== null) return;
    const first = communitiesQuery.data?.[0];
    if (first) setActiveCommunityId(first.id);
  }, [activeCommunityId, communitiesQuery.data, setActiveCommunityId]);

  const contentQuery = useContentQuery(activeCommunityId);

  const header = (
    <header className="flex items-center justify-between gap-3 pb-2">
      <h1 className="text-lg font-semibold">{CONTENT_PAGE_STRINGS.pageTitle}</h1>
      <div className="flex items-center gap-2">
        {communitiesQuery.data && communitiesQuery.data.length > 0 ? (
          <CommunitySelector
            communities={communitiesQuery.data}
            activeCommunityId={activeCommunityId}
            onSelect={setActiveCommunityId}
            label="Active community"
          />
        ) : null}
      </div>
    </header>
  );

  // 1) communities list errored — page-wide error state.
  if (communitiesQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <ContentErrorState
          title={CONTENT_PAGE_STRINGS.errorTitle}
          body={
            communitiesQuery.error?.message ??
            CONTENT_PAGE_STRINGS.errorBodyDefault
          }
          retryLabel={CONTENT_PAGE_STRINGS.retry}
          onRetry={() => void communitiesQuery.refetch()}
        />
      </main>
    );
  }

  // 2) communities list resolved → user has zero communities → onboarding.
  if (communitiesQuery.data && communitiesQuery.data.length === 0) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <ContentEmptyState
          title={CONTENT_PAGE_STRINGS.noCommunity.title}
          body={CONTENT_PAGE_STRINGS.noCommunity.body}
        />
      </main>
    );
  }

  // 3) content query errored — single retry CTA.
  if (contentQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <ContentErrorState
          title={CONTENT_PAGE_STRINGS.errorTitle}
          body={contentQuery.error?.message ?? CONTENT_PAGE_STRINGS.errorBodyDefault}
          retryLabel={CONTENT_PAGE_STRINGS.retry}
          onRetry={() => void contentQuery.refetch()}
        />
      </main>
    );
  }

  // 4) Loading — composite skeleton beneath the header so layout doesn't
  // shift when the data lands.
  if (
    !communitiesQuery.data ||
    !contentQuery.data ||
    activeCommunityId === null
  ) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <ContentSkeleton loadingLabel={CONTENT_PAGE_STRINGS.loading} />
      </main>
    );
  }

  const content = contentQuery.data;

  // 5a) Empty state — backend tells us when there's no data in the window.
  // Trust the flag rather than re-deriving from the empty arrays (which
  // would also be empty for a brand-new community on day 1).
  if (!content.dataAvailable) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <ContentEmptyState
          title={CONTENT_PAGE_STRINGS.empty.title}
          body={CONTENT_PAGE_STRINGS.empty.body}
        />
      </main>
    );
  }

  // 5b) Happy path.
  return (
    <main className="container flex min-h-screen flex-col gap-6 py-4">
      {header}
      <ActivityHeatmap cells={content.activityHeatmap} />
      <TopPostsBoard posts={content.topPosts} />
      <OptimalSlotsPanel result={content.optimalSlots} />
    </main>
  );
}
